import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { ListParams } from '@/types/api'
import type { ExamFormOutput, GradingScaleFormOutput, PaperFormOutput } from './schemas'
import type {
  Exam,
  ExamTransition,
  ExamType,
  FamilyExamResult,
  GradingAssignment,
  GradingScale,
  GradingTemplate,
  MarkEntry,
  MarkSheet,
  Paper,
  ReportCard,
  Tabulation,
} from './types'

export const examKeys = {
  all: ['exams'] as const,
  list: (params: object) => [...examKeys.all, 'list', params] as const,
  detail: (id: number) => [...examKeys.all, 'detail', id] as const,
  papers: (id: number) => [...examKeys.all, 'detail', id, 'papers'] as const,
  sheet: (examId: number, paperId: number, sectionId: number) =>
    [...examKeys.all, 'detail', examId, 'sheet', paperId, sectionId] as const,
  results: (examId: number, classId: number | undefined, sectionId: number | undefined) =>
    [...examKeys.all, 'detail', examId, 'results', classId, sectionId] as const,
  reportCard: (examId: number, studentId: number) =>
    [...examKeys.all, 'detail', examId, 'report-card', studentId] as const,
  scales: () => [...examKeys.all, 'scales'] as const,
  templates: () => [...examKeys.all, 'templates'] as const,
  types: () => [...examKeys.all, 'types'] as const,
  grading: () => [...examKeys.all, 'my-grading'] as const,
  family: (studentId: number) => [...examKeys.all, 'family', studentId] as const,
  familyCard: (studentId: number, examId: number) => [...examKeys.all, 'family', studentId, examId] as const,
}

export const examsQuery = (params: ListParams & Record<string, string | number | undefined>) =>
  queryOptions({
    queryKey: examKeys.list(params),
    queryFn: () => api.page<Exam>('/exams', params),
    placeholderData: keepPreviousData,
  })
export const examQuery = (id: number) =>
  queryOptions({ queryKey: examKeys.detail(id), queryFn: () => api.get<Exam>(`/exams/${id}`) })
export const papersQuery = (examId: number) =>
  queryOptions({
    queryKey: examKeys.papers(examId),
    queryFn: () => api.get<Paper[]>(`/exams/${examId}/papers`),
  })
export const markSheetQuery = (examId: number, paperId: number, sectionId: number) =>
  queryOptions({
    queryKey: examKeys.sheet(examId, paperId, sectionId),
    queryFn: () => api.get<MarkSheet>(`/exams/${examId}/papers/${paperId}/marks`, { params: { sectionId } }),
  })
export const tabulationQuery = (examId: number, classId?: number, sectionId?: number) =>
  queryOptions({
    queryKey: examKeys.results(examId, classId, sectionId),
    queryFn: () => api.get<Tabulation>(`/exams/${examId}/results`, { params: { classId, sectionId } }),
    placeholderData: keepPreviousData,
  })
export const reportCardQuery = (examId: number, studentId: number) =>
  queryOptions({
    queryKey: examKeys.reportCard(examId, studentId),
    queryFn: () => api.get<ReportCard>(`/exams/${examId}/students/${studentId}/report-card`),
  })
export const gradingScalesQuery = () =>
  queryOptions({ queryKey: examKeys.scales(), queryFn: () => api.get<GradingScale[]>('/grading-scales') })
export const gradingTemplatesQuery = () =>
  queryOptions({
    queryKey: examKeys.templates(),
    queryFn: () => api.get<GradingTemplate[]>('/grading-scales/templates'),
    staleTime: Infinity,
  })
export const examTypesQuery = () =>
  queryOptions({ queryKey: examKeys.types(), queryFn: () => api.get<ExamType[]>('/exam-types') })
export const myGradingQuery = () =>
  queryOptions({
    queryKey: examKeys.grading(),
    queryFn: () => api.get<GradingAssignment[]>('/me/staff/exams'),
  })
export const familyResultsQuery = (studentId: number) =>
  queryOptions({
    queryKey: examKeys.family(studentId),
    queryFn: () => api.get<FamilyExamResult[]>(`/me/family/students/${studentId}/exams`),
  })
export const familyReportCardQuery = (studentId: number, examId: number) =>
  queryOptions({
    queryKey: examKeys.familyCard(studentId, examId),
    queryFn: () => api.get<ReportCard>(`/me/family/students/${studentId}/exams/${examId}`),
  })

/** Exam data is interlinked (progress, results, statuses): every write refreshes the whole area. */
function useExamMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: examKeys.all })
      void qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export const useSaveExam = () =>
  useExamMutation(({ id, ...body }: ExamFormOutput & { id?: number }) =>
    id ? api.put<Exam>(`/exams/${id}`, body) : api.post<Exam>('/exams', body),
  )
export const useDeleteExam = () => useExamMutation((id: number) => api.delete(`/exams/${id}`))
export const useExamTransition = () =>
  useExamMutation(
    ({
      id,
      transition,
      allowIncomplete,
    }: {
      id: number
      transition: ExamTransition
      allowIncomplete?: boolean
    }) => api.post<Exam>(`/exams/${id}/${transition}`, allowIncomplete ? { allowIncomplete } : undefined),
  )

export const useSavePaper = () =>
  useExamMutation(({ examId, id, ...body }: PaperFormOutput & { examId: number; id?: number }) =>
    id
      ? api.put<Paper>(`/exams/${examId}/papers/${id}`, body)
      : api.post<Paper>(`/exams/${examId}/papers`, body),
  )
export const useDeletePaper = () =>
  useExamMutation(({ examId, id }: { examId: number; id: number }) =>
    api.delete(`/exams/${examId}/papers/${id}`),
  )
export const useGeneratePapers = () =>
  useExamMutation(
    ({
      examId,
      ...body
    }: {
      examId: number
      classIds: number[]
      maxMarks: number
      passMarks: number
      includeOptional: boolean
    }) => api.post<{ created: number; papers: Paper[] }>(`/exams/${examId}/papers/generate`, body),
  )

export const useSaveMarks = () =>
  useExamMutation(
    ({
      examId,
      paperId,
      sectionId,
      entries,
    }: {
      examId: number
      paperId: number
      sectionId: number
      entries: MarkEntry[]
    }) =>
      api.put<{ saved: number; cleared: number; sheet: MarkSheet }>(
        `/exams/${examId}/papers/${paperId}/marks`,
        { sectionId, entries },
      ),
  )

export const useSaveGradingScale = () =>
  useExamMutation(({ id, ...body }: GradingScaleFormOutput & { id?: number }) =>
    id
      ? api.put<GradingScale>(`/grading-scales/${id}`, body)
      : api.post<GradingScale>('/grading-scales', body),
  )
export const useDeleteGradingScale = () =>
  useExamMutation((id: number) => api.delete(`/grading-scales/${id}`))
export const useSaveExamType = () =>
  useExamMutation(({ id, ...body }: { id?: number; name: string; description: string | null }) =>
    id ? api.put<ExamType>(`/exam-types/${id}`, body) : api.post<ExamType>('/exam-types', body),
  )
